/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useRef } from 'react';
import { useActivation } from '@context/MobileOne';
import {
  ButtonPrimary,
  CardSupport,
  FormInputField,
  Notification,
  ScrollToTop,
  ButtonCancelActivation,
  FocusFirstError
} from '@core/index';
import { AppPageContentWrapper } from '@part/AppPageContentWrapper';
import { appAlert, appButtonType, isOnlySpecificKeyInObjTrue } from '@utils/globalConstant';
import { tA11y } from '@utils/a11y/a11yHelpers';
import { Form, Formik } from 'formik';

export function LoginPasswordActivation() {
  // Contexts
  const {
    isLoading,
    loginPasswordForm,
    validatePasswordWithKeys,
    loginPasswordFormSubmit,
    changePasswordErrorMsg,
    setChangePasswordErrorMsg
  } = useActivation();

  // Default info notifications are visible after the page loads
  const infoNotifications = [
    { key: 'minMax', message: 'ek_global_pw_string-length_info' },
    { key: 'uppercase', message: 'ek_global_pw_upper-or-lower-case_info' },
    { key: 'number', message: 'ek_global_pw_numbers_info' },
    { key: 'specific', message: 'ek_global_pw_special-chars_info' }
  ];

  const errorNotifications = [
    { key: 'minMax', message: 'ek_global_pw_string-length_err' },
    { key: 'uppercase', message: 'ek_global_pw_upper-or-lower-case_err' },
    { key: 'number', message: 'ek_global_pw_numbers_err' },
    { key: 'specific', message: 'ek_global_pw_special-chars_err' }
  ];

  const successNotifications = [
    { key: 'minMax', message: 'ek_global_pw_string-length_suc' },
    { key: 'uppercase', message: 'ek_global_pw_upper-or-lower-case_suc' },
    { key: 'number', message: 'ek_global_pw_numbers_suc' },
    { key: 'specific', message: 'ek_global_pw_special-chars_suc' }
  ];

  // Utility function to determine notification type
  const getNotificationType = (key, errors, value, touched) => {
    // Handle case when the field has a value
    if (value && errors?.otherErrors?.[key]) {
      return {
        type: appAlert.ERROR,
        message: errorNotifications.find((notification) => notification.key === key).message
      };
    }

    if (value && !errors?.otherErrors?.[key]) {
      return {
        type: appAlert.SUCCESS,
        message: successNotifications.find((notification) => notification.key === key).message
      };
    }

    // Handle case when the field is empty
    if (errors.cscPassword === 'Pflichtfeld') {
      return {
        type: appAlert.ERROR,
        message: errorNotifications.find((notification) => notification.key === key).message
      };
    }

    // Handle case when the field is empty and has no errors (on first load showing hint message)
    return {
      type: '',
      message: infoNotifications.find((notification) => notification.key === key)?.message
    };
  };

  return (
    <>
      <ScrollToTop>
        <div className="min-dvh-100">
          <AppPageContentWrapper>
            <div className="row">
              {tA11y('ek_reg_pw_hdl1').isHtml ? (
                <h2
                  className="text-primary m-0 p-0 mb-40"
                  dangerouslySetInnerHTML={{
                    __html: tA11y('ek_reg_pw_hdl1').content
                  }}
                />
              ) : (
                <h2 className="text-primary m-0 p-0 mb-40">{tA11y('ek_reg_pw_hdl1').content}</h2>
              )}

              <Formik
                initialValues={loginPasswordForm}
                validate={(v) => validatePasswordWithKeys(v, 'cscPassword', 'confirmCscPassword')}
                onSubmit={loginPasswordFormSubmit}>
                {({
                  values: { cscPassword, confirmCscPassword },
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  touched,
                  errors
                  // eslint-disable-next-line arrow-body-style
                }) => {
                  // Behavior in cscPassword field:
                  // if specificKey "match" from errors.otherErrors obj is true and all other keys are false,
                  // success messages are shown and focus is set to the next field (confirmCscPassword)
                  const specificKey = 'match';
                  const isSpecificKeyValid = isOnlySpecificKeyInObjTrue(
                    errors.otherErrors,
                    specificKey
                  );

                  return (
                    <Form className="p-0" onSubmit={handleSubmit}>
                      <FormInputField
                        label="ek_global_set_pw_lbl"
                        placeholder="ek_global_set_pw_plc"
                        type="password"
                        autoComplete="new-password" // https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#new-password
                        id="cscPassword"
                        name="cscPassword"
                        value={cscPassword}
                        onChange={(e) => {
                          handleChange({
                            target: {
                              id: 'cscPassword',
                              name: 'cscPassword',
                              value: e.target.value
                            }
                          });
                          setChangePasswordErrorMsg('');
                        }}
                        onBlur={handleBlur}
                        invalid={
                          errors.cscPassword === 'Pflichtfeld' ||
                          (errors.otherErrors && !isSpecificKeyValid)
                        }
                        aria-describedby="login-password-error"
                      />
                      <ul
                        className="form-text pt-4 ps-4 mb-32 list-unstyled"
                        id="login-password-error">
                        {infoNotifications.map(({ key }) => {
                          const notification = getNotificationType(key, errors, cscPassword);
                          return (
                            <li key={key} style={{ listStyleType: 'none' }}>
                              <Notification
                                key={key}
                                type={notification.type}
                                message={tA11y(notification.message).content}
                                isLiveRegion={false}
                              />
                            </li>
                          );
                        })}
                        {/* validation: full regex */}
                        {(changePasswordErrorMsg || errors?.otherErrors?.invalid) && (
                          <li>
                            <Notification
                              type={appAlert.ERROR}
                              message={
                                changePasswordErrorMsg ||
                                tA11y('ek_global_pw_invalid-char_err').content
                              }
                              isLiveRegion={false}
                            />
                          </li>
                        )}
                      </ul>

                      <FormInputField
                        label="ek_global_match_pw_lbl"
                        placeholder="ek_global_match_pw_plc"
                        type="password"
                        autoComplete="new-password"
                        id="confirmCscPassword"
                        name="confirmCscPassword"
                        value={confirmCscPassword}
                        onChange={(e) => {
                          handleChange({
                            target: {
                              id: 'confirmCscPassword',
                              name: 'confirmCscPassword',
                              value: e.target.value
                            }
                          });
                          setChangePasswordErrorMsg('');
                        }}
                        onBlur={handleBlur}
                        valid={touched.confirmCscPassword && !errors.confirmCscPassword}
                        invalid={touched.confirmCscPassword && errors.confirmCscPassword}
                        invalidMessage={
                          touched.confirmCscPassword && errors.confirmCscPassword
                            ? 'ek_global_pw_match_err'
                            : null
                        }
                        validMessage={
                          touched.confirmCscPassword && !errors.confirmCscPassword
                            ? 'ek_global_pw_match_suc'
                            : null
                        }
                        // hint="ek_global_match_pw_info" // issue: this hint was read by the SR before other content
                        aria-describedby={
                          touched.confirmCscPassword && errors.confirmCscPassword
                            ? 'confirmCscPassword-error'
                            : !touched.confirmCscPassword
                            ? 'confirmCscPassword-hint'
                            : null
                        }
                      />
                      {/* hint message */}
                      {!(
                        (touched.confirmCscPassword && errors.confirmCscPassword) ||
                        (touched.confirmCscPassword && !errors.confirmCscPassword)
                      ) && (
                        <div
                          id="confirmCscPassword-hint"
                          className="form-text pt-4 ps-4"
                          aria-hidden={!touched.confirmCscPassword}>
                          <Notification message="ek_global_match_pw_info" />
                        </div>
                      )}
                      <div className="row">
                        <div className="col-xxl-5 col-lg-5 col-10 px-0 mx-auto my-40">
                          <ButtonPrimary
                            isLoading={isLoading}
                            type="submit"
                            buttonContent={tA11y('ek_reg_pw_btn1')}
                            buttonType={appButtonType.PRIMARY}
                            label="ek_reg_pw_btn1"
                          />
                        </div>
                      </div>
                      <FocusFirstError />
                    </Form>
                  );
                }}
              </Formik>
              <CardSupport />
              <div className="d-flex justify-content-center mt-32">
                <ButtonCancelActivation />
              </div>
            </div>
          </AppPageContentWrapper>
        </div>
      </ScrollToTop>
    </>
  );
}

export default LoginPasswordActivation;
